import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import { getProviderOfficeExamRoom } from 'store/slices/patientInfoSlice';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { SelectionList, UserLocation, ProfileSetting } from './userOffice.types';

interface Props {
  value: UserLocation[];
  locations: SelectionList[];
  profiles: SelectionList[];
  submitAttempted: boolean;
  onChange: React.Dispatch<React.SetStateAction<UserLocation[]>>;
  onEditProfile: (locIndex: number, profIndex: number) => void;
}

export default function UserLocationSelector({
  value,
  locations,
  profiles,
  submitAttempted,
  onChange,
  onEditProfile,
}: Props) {
  const dispatch = useAppDispatch();
  const [loadingLocationIndex, setLoadingLocationIndex] = useState<number | null>(null);

  /* ================= SAFE FUNCTIONAL UPDATE ================= */
  const update = (updater: (prev: UserLocation[]) => UserLocation[]) => {
    onChange((prev) => updater(prev));
  };

  /* ================= DELETE ================= */
  const [confirmState, setConfirmState] = useState<{
    type: 'profile' | 'location' | null;
    locIndex?: number;
    profIndex?: number;
  }>({ type: null });

  const handleConfirmDelete = () => {
    update((prev) => {
      const copy = [...prev];

      if (confirmState.type === 'location' && confirmState.locIndex !== undefined) {
        copy.splice(confirmState.locIndex, 1);
      }

      if (
        confirmState.type === 'profile' &&
        confirmState.locIndex !== undefined &&
        confirmState.profIndex !== undefined
      ) {
        copy[confirmState.locIndex].profiles.splice(confirmState.profIndex, 1);
      }

      return copy;
    });

    setConfirmState({ type: null });
  };

  const fetchExamRooms = async (officeId: number, locIndex: number) => {
    setLoadingLocationIndex(locIndex);

    try {
      const res = await dispatch(getProviderOfficeExamRoom({ officeId })).unwrap();

      const normalizedRooms = (res.data || []).map((r: any) => ({
        id: r.index, // from API
        name: r.name,
      }));

      update((prev) => {
        const copy = [...prev];

        copy[locIndex] = {
          ...copy[locIndex],
          examRooms: normalizedRooms,
          defaultExamRoomId: '', // 🔥 reset when new location selected
          profiles: copy[locIndex].profiles.map((p) => ({
            ...p,
            secondaryExamRoomId: '', // reset overrides too
          })),
        };

        return copy;
      });
    } finally {
      setLoadingLocationIndex(null);
    }
  };

  /* ================= PROFILE COMPLETION ================= */
  const isProfileIncomplete = (p: ProfileSetting) =>
    !p.profileId || !p.secondaryExamRoomId || !p.scheduleTime || !p.duration || !p.weekDays?.length;

  /* ================= RENDER ================= */
  return (
    <Box mt={3}>
      <Stack direction='row' justifyContent='space-between' mb={2}>
        <Typography variant='h6'>Location Settings</Typography>

        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            update((prev) => [
              ...prev,
              {
                locationId: '',
                defaultExamRoomId: '',
                examRooms: [],
                profiles: [
                  {
                    profileId: '',
                    secondaryExamRoomId: '',
                    isEditing: true,
                  },
                ],
              },
            ])
          }
        >
          Add Location
        </Button>
      </Stack>

      {value.map((loc, li) => (
        <Box key={li} border='1px solid #ddd' p={2} mb={2}>
          {/* ================= LOCATION ================= */}
          <Stack direction='row' spacing={2}>
            <TextField
              select
              fullWidth
              label='Location'
              value={loc.locationId}
              onChange={(e) => {
                const officeId = Number(e.target.value);

                // 🔥 Prevent duplicate selection
                const alreadySelected = value.some(
                  (existingLoc, index) => index !== li && existingLoc.locationId === officeId
                );

                if (alreadySelected) {
                  return; // block duplicate
                }

                update((prev) => {
                  const copy = [...prev];
                  copy[li] = {
                    ...copy[li],
                    locationId: officeId,
                    defaultExamRoomId: '',
                    examRooms: [],
                  };
                  return copy;
                });

                fetchExamRooms(officeId, li);
              }}
            >
              <MenuItem value='' disabled>
                Select Location
              </MenuItem>

              {locations.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.city || l.name}
                </MenuItem>
              ))}
            </TextField>

            <IconButton
              color='error'
              onClick={() => setConfirmState({ type: 'location', locIndex: li })}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>

          {/* ================= DEFAULT EXAM ROOM ================= */}
          {loc.locationId && (
            <TextField
              select
              fullWidth
              sx={{ mt: 2 }}
              label='Default Exam Room'
              value={loc.defaultExamRoomId ?? ''}
              error={submitAttempted && !loc.defaultExamRoomId}
              helperText={
                submitAttempted && !loc.defaultExamRoomId ? 'Default exam room is required' : ''
              }
              onChange={(e) => {
                const roomId = Number(e.target.value);

                update((prev) =>
                  prev.map((location, index) =>
                    index !== li
                      ? location
                      : {
                          ...location,
                          defaultExamRoomId: roomId,
                          profiles: location.profiles.map((p) => ({
                            ...p,
                            secondaryExamRoomId: p.secondaryExamRoomId || roomId,
                          })),
                        }
                  )
                );
              }}
            >
              <MenuItem value='' disabled>
                Select Default Exam Room
              </MenuItem>

              {loc.examRooms?.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {room.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {/* ================= PROFILES ================= */}
          {loc.profiles.map((p, pi) => (
            <Grid container spacing={2} alignItems='center' key={pi} mt={2}>
              <Grid item xs={5}>
                <TextField
                  select
                  fullWidth
                  label='Appointment Template'
                  value={p.profileId}
                  onChange={(e) =>
                    update((prev) => {
                      const copy = [...prev];
                      copy[li].profiles[pi].profileId = Number(e.target.value);
                      copy[li].profiles[pi].isEditing = false;
                      return copy;
                    })
                  }
                >
                  <MenuItem value='' disabled>
                    Select Appointment Template
                  </MenuItem>

                  {profiles.map((prof) => (
                    <MenuItem key={prof.id} value={prof.id}>
                      {prof.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={1}>
                <IconButton onClick={() => onEditProfile(li, pi)}>
                  <EditIcon />
                </IconButton>
              </Grid>

              <Grid item xs={1}>
                <IconButton
                  color='error'
                  onClick={() =>
                    setConfirmState({
                      type: 'profile',
                      locIndex: li,
                      profIndex: pi,
                    })
                  }
                >
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Button
            size='small'
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            onClick={() =>
              update((prev) => {
                const copy = [...prev];
                copy[li].profiles.push({
                  profileId: '',
                  secondaryExamRoomId: '',
                  isEditing: true,
                });
                return copy;
              })
            }
          >
            Add Appointment Template
          </Button>
        </Box>
      ))}

      <ConfirmDeleteDialog
        open={confirmState.type !== null}
        title='Confirm Delete'
        message='Are you sure?'
        onCancel={() => setConfirmState({ type: null })}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}
